import React, { useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { KeyRound, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modal shown when a user logs in with a temporary password set by an admin.
 * The user cannot dismiss it — they must set their own password before continuing.
 * On successful change, `must_change_password` becomes false and the modal closes.
 */
export default function MandatoryPasswordChange() {
  const { user, logout } = useAuth();
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [saving, setSaving] = useState(false);

  const mustChange = !!user?.must_change_password;

  if (!mustChange) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error('La nuova password deve avere almeno 8 caratteri'); return; }
    if (newPwd !== confirmPwd) { toast.error('Le due password non coincidono'); return; }
    if (newPwd === currentPwd) { toast.error('La nuova password deve essere diversa dalla temporanea'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success('Password aggiornata. Ora usi la tua password personale.');
      // Force full reload so /auth/me is refetched → must_change_password becomes false
      window.location.reload();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Errore aggiornamento password');
      setSaving(false);
    }
  };

  return (
    <Dialog open={mustChange} onOpenChange={() => { /* not dismissible */ }}>
      <DialogContent
        className="max-w-md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
            <ShieldAlert size={20} className="text-amber-600" />
            Cambio password obbligatorio
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 mb-3">
          <p className="text-sm text-amber-900 dark:text-amber-100">
            La tua password attuale è <b>temporanea</b>, impostata da un amministratore.
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-1">
            Per motivi di sicurezza devi sceglierne una tua prima di continuare.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Password temporanea (attuale) *</Label>
            <Input
              type="password" required autoFocus
              value={currentPwd}
              onChange={e => setCurrentPwd(e.target.value)}
              data-testid="current-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Nuova password (min 8 caratteri) *</Label>
            <Input
              type="password" required minLength={8}
              value={newPwd}
              onChange={e => setNewPwd(e.target.value)}
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Conferma nuova password *</Label>
            <Input
              type="password" required minLength={8}
              value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
              data-testid="confirm-password-input"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button" variant="outline"
              onClick={() => { if (window.confirm('Vuoi uscire senza cambiare la password? Al prossimo accesso ti sarà richiesta nuovamente.')) logout(); }}
              className="flex-shrink-0"
            >Esci</Button>
            <Button type="submit" className="flex-1" disabled={saving} data-testid="mandatory-change-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <KeyRound size={14} className="mr-2" />
              Imposta nuova password
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
